import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SegmentedControl, type SegmentedControlOption } from '@/components/ui/segmented-control';
import {
  BASE_GARMENTS_VIEW,
  withBaseGarmentsView,
  withoutBaseGarmentsParams,
} from '@/features/base-garments/lib/base-garments-url';
import { useProductsViews } from '../hooks/useProductsViews';
import {
  CLIENT_PRICES_VIEW,
  withClientPricesView,
  withoutClientPricesParams,
} from '../lib/client-prices-url';
import { LeaveUnsavedPricesDialog } from './LeaveUnsavedPricesDialog';

export type ProductsView = 'catalogue' | typeof BASE_GARMENTS_VIEW | typeof CLIENT_PRICES_VIEW;

/**
 * Products: `Products | Base garments | Client prices`, the first control of the
 * page's action bar on every view. A `SegmentedControl` (role="group" +
 * aria-pressed), not Radix Tabs: it swaps whole views and owns no tab panels,
 * so a tablist would leave `aria-controls` pointing at nothing.
 *
 * Each optional view shows only to whoever may open it (`useProductsViews`:
 * the same checks the views make, so the switch never offers a view that
 * refuses).
 *
 * It writes `?view=` (a history entry, so Back returns to the other view).
 * Leaving a view drops all of its params (Client prices' `p*` and `tab`, Base
 * garments' `b*` and `code`). With unsaved edits it asks first; the edits are
 * lost on leaving, so Leave is the red button and Keep editing has the focus.
 */
export function ProductsViewSwitch({ value, dirtyCount = 0 }: { value: ProductsView; dirtyCount?: number }) {
  const { t } = useTranslation('inventory');
  const [, setSearchParams] = useSearchParams();
  const views = useProductsViews();
  const [leaveTo, setLeaveTo] = useState<ProductsView | null>(null);

  const go = (next: ProductsView) =>
    setSearchParams((prev) => {
      const cleared = withoutBaseGarmentsParams(withoutClientPricesParams(prev));
      if (next === CLIENT_PRICES_VIEW) return withClientPricesView(cleared);
      if (next === BASE_GARMENTS_VIEW) return withBaseGarmentsView(cleared);
      return cleared;
    });

  const handleChange = (next: ProductsView) => {
    if (next === value) return;
    if (dirtyCount > 0) {
      setLeaveTo(next);
      return;
    }
    go(next);
  };

  // The view on screen is always listed, so the control never loses its value.
  const options: Array<SegmentedControlOption<ProductsView>> = [
    { value: 'catalogue', label: t('clientPrices.view.catalogue') },
  ];
  if (views.baseGarments || value === BASE_GARMENTS_VIEW) {
    options.push({ value: BASE_GARMENTS_VIEW, label: t('baseGarments.view.title') });
  }
  if (views.clientPrices || value === CLIENT_PRICES_VIEW) {
    options.push({ value: CLIENT_PRICES_VIEW, label: t('clientPrices.view.clientPrices') });
  }

  return (
    <>
      <SegmentedControl<ProductsView>
        options={options}
        value={value}
        onChange={handleChange}
        ariaLabel={t('clientPrices.view.aria')}
        className="self-end"
      />
      <LeaveUnsavedPricesDialog
        open={leaveTo !== null}
        onOpenChange={(open) => {
          if (!open) setLeaveTo(null);
        }}
        count={dirtyCount}
        onLeave={() => {
          if (leaveTo !== null) go(leaveTo);
        }}
      />
    </>
  );
}
